// Bezugspersonenbogen — /bezugsperson?t=<link token>
//
// The patient names one Bezugsperson per submission (partner, parent, friend …): who the person is,
// the relationship, whether they may accompany the patient and give information, contact details,
// and three consents. Several persons: submit once per person. Its own session (X-Related-Person-Token),
// no questionnaire. Each person is written to PsyCloud; "Ja" to the Schweigepflichtentbindung leads
// straight into the SMS-signed waiver for that person (/informed-consent). Spec: docs/bezugspersonenbogen.md
import { FormEvent, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AlertTriangle, CheckCircle2, Loader2, PenLine, ShieldCheck, UserPlus, Users } from 'lucide-react'
import { Logo } from '../components/Logo'
import { Button } from '../components/Button'
import { LegalFooter } from '../components/LegalFooter'
import { SearchableSelect } from '../components/SearchableSelect'
import { StepIndicator } from '../components/StepIndicator'
import { useTenant } from '../context/TenantContext'
import {
  relatedPersonService, RelatedPersonAnswers, RelatedPersonLinkSummary, RelatedPersonSubmitResult, getApiErrorCode, getApiErrorDetails,
} from '../services/api'
import { cn } from '../utils/cn'
import {
  validateName, formatName, validateCity, validatePostalCode, formatPostalCode, validatePhoneNumber, normalizePhoneNumberOnBlur,
  isValidEmail, streetEndsWithHouseNumber,
} from '../utils/validation'
import { buildCountriesList } from '../data/countries'

type Phase = 'loading' | 'invalid' | 'expired' | 'revoked' | 'disabled' | 'ready' | 'done'

// Stored values = PsyCloud GuardianRelationshipType, in the order of the spec
const RELATIONSHIPS = [
  'partner', 'mother', 'father', 'parent', 'sibling', 'adult_child', 'grandparent', 'friend',
  'legal_custodian', 'authorized_representative', 'legal_guardian_minor', 'foster_parent', 'step_parent', 'other',
] as const
const GENDERS = ['female', 'male', 'diverse'] as const
// Only a legal representative has Aufgabenkreise (question 7)
const LEGAL_ROLES = new Set(['legal_custodian', 'authorized_representative', 'legal_guardian_minor'])

// Yes/no questions start unanswered: the patient answers each one
type YesNo = 'yes' | 'no' | ''
type FormState = Omit<RelatedPersonAnswers, 'referencePerson' | 'confidentialityWaiver' | 'contactAgreed'> & {
  referencePerson: YesNo; confidentialityWaiver: YesNo; contactAgreed: YesNo
}
const EMPTY: FormState = {
  firstName: '', lastName: '', gender: null, relationshipType: '', relationshipNote: '', referencePerson: '', scope: '',
  phone: '', email: '', street: '', houseNumber: '', zipCode: '', city: '', state: '', country: '',
  confidentialityWaiver: '', contactAgreed: '', privacyAccepted: false,
}
type Field = keyof FormState

export function RelatedPersonPage() {
  const { t, i18n } = useTranslation()
  const { tenant, loading: tenantLoading } = useTenant()

  const [phase, setPhase] = useState<Phase>('loading')
  const [session, setSession] = useState<string | null>(null)
  const [link, setLink] = useState<RelatedPersonLinkSummary | null>(null)
  const [form, setForm] = useState<FormState>(EMPTY)
  // Most Bezugspersonen live in the patient's country: start with the organisation's default
  useEffect(() => {
    if (tenant?.defaultCountry) setForm(prev => (prev.country ? prev : { ...prev, country: tenant.defaultCountry }))
  }, [tenant?.defaultCountry])
  const [errors, setErrors] = useState<Partial<Record<Field, string>>>({})
  const [busy, setBusy] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [result, setResult] = useState<RelatedPersonSubmitResult | null>(null)

  const linkToken = new URLSearchParams(window.location.search).get('t') || ''

  useEffect(() => {
    if (tenantLoading) return
    if (!linkToken) { setPhase('invalid'); return }
    let cancelled = false
    ;(async () => {
      try {
        const { sessionToken, link: summary } = await relatedPersonService.exchange(linkToken)
        if (cancelled) return
        if (summary.locale && summary.locale !== i18n.language) i18n.changeLanguage(summary.locale)
        setSession(sessionToken)
        setLink(summary)
        setPhase('ready')
      } catch (err) {
        if (cancelled) return
        const code = getApiErrorCode(err)
        // Explain it in the language the link was sent in
        const linkLocale = getApiErrorDetails(err)?.locale
        if (typeof linkLocale === 'string' && linkLocale !== i18n.language) i18n.changeLanguage(linkLocale)
        setPhase(code === 'link_expired' ? 'expired' : code === 'link_revoked' ? 'revoked' : code === 'form_disabled' ? 'disabled' : 'invalid')
      }
    })()
    return () => { cancelled = true }
  }, [tenantLoading, linkToken, i18n])

  const set = <K extends Field>(key: K, value: FormState[K]) => {
    setForm(prev => {
      const next = { ...prev, [key]: value }
      // The description only belongs to "Sonstige", the Aufgabenkreise only to a legal representative
      if (key === 'relationshipType') {
        if (value !== 'other') next.relationshipNote = ''
        if (!LEGAL_ROLES.has(String(value))) next.scope = ''
      }
      return next
    })
    setErrors(prev => ({
      ...prev, [key]: undefined,
      ...(key === 'phone' || key === 'email' ? { phone: undefined, email: undefined } : {}),
      ...(['street', 'houseNumber', 'zipCode', 'city', 'country'].includes(key) ? { street: undefined, houseNumber: undefined, zipCode: undefined, city: undefined } : {}),
    }))
  }

  const validate = (): boolean => {
    const e: Partial<Record<Field, string>> = {}
    const required = t('relatedPerson.errors.required')
    // Names: the same rules as the patient's own name in the questionnaire
    e.firstName = validateName(form.firstName, t('relatedPerson.fields.firstName'))
    e.lastName = validateName(form.lastName, t('relatedPerson.fields.lastName'))
    if (!form.gender) e.gender = required
    if (!form.relationshipType) e.relationshipType = required
    if (form.relationshipType === 'other' && !form.relationshipNote.trim()) e.relationshipNote = t('relatedPerson.errors.describeRelationship')
    if (!form.referencePerson) e.referencePerson = required
    if (!form.phone.trim() && !form.email.trim()) e.phone = t('relatedPerson.errors.contactNeeded')
    if (form.phone.trim()) {
      const p = validatePhoneNumber(form.phone)
      if (!p.valid) e.phone = p.error
    }
    if (form.email.trim() && !isValidEmail(form.email)) e.email = t('relatedPerson.errors.email')
    // Address: optional — but once started, complete and plausible
    const addressStarted = [form.street, form.houseNumber, form.zipCode, form.city].some(v => v.trim())
    if (addressStarted) {
      const street = form.street.trim()
      if (!street) e.street = required
      else if (!/\p{L}{2,}/u.test(street)) e.street = t('relatedPerson.errors.street')
      else if (streetEndsWithHouseNumber(street) && !form.houseNumber.trim()) e.street = t('relatedPerson.errors.streetNumber')
      if (!form.houseNumber.trim()) e.houseNumber = required
      else if (!/\d/.test(form.houseNumber)) e.houseNumber = t('relatedPerson.errors.houseNumber')
      e.zipCode = validatePostalCode(form.zipCode, form.country || 'Other')
      e.city = validateCity(form.city, t('relatedPerson.fields.city'))
    }
    if (!form.confidentialityWaiver) e.confidentialityWaiver = required
    else if (form.confidentialityWaiver === 'no') e.confidentialityWaiver = t('relatedPerson.errors.waiverNeeded')
    if (!form.contactAgreed) e.contactAgreed = required
    else if (form.contactAgreed === 'no') e.contactAgreed = t('relatedPerson.errors.contactAgreedNeeded')
    if (!form.privacyAccepted) e.privacyAccepted = t('relatedPerson.errors.privacy')
    const found = Object.fromEntries(Object.entries(e).filter(([, v]) => v)) as Partial<Record<Field, string>>
    setErrors(found)
    return Object.keys(found).length === 0
  }

  const submit = async (ev: FormEvent) => {
    ev.preventDefault()
    setSubmitError(null)
    if (!validate()) {
      setSubmitError(t('relatedPerson.errors.checkFields'))
      return
    }
    if (!session) return
    setBusy(true)
    try {
      const r = await relatedPersonService.submit(session, form as RelatedPersonAnswers)
      setResult(r)
      setLink(r.link)
      setPhase('done')
      window.scrollTo({ top: 0, behavior: 'smooth' })
    } catch (err) {
      const code = getApiErrorCode(err)
      if (code === 'link_expired') setPhase('expired')
      else if (code === 'link_revoked') setPhase('revoked')
      else if (code === 'related_person_session_expired' || code === 'related_person_session_required') {
        // the session (2h) ran out while filling in: get a fresh one from the link and try once more
        try {
          const fresh = await relatedPersonService.exchange(linkToken)
          setSession(fresh.sessionToken)
          const r = await relatedPersonService.submit(fresh.sessionToken, form as RelatedPersonAnswers)
          setResult(r); setLink(r.link); setPhase('done')
        } catch {
          setSubmitError(t('relatedPerson.errors.generic'))
        }
      } else if (code === 'too_many_entries') setSubmitError(t('relatedPerson.errors.tooMany'))
      else setSubmitError(t('relatedPerson.errors.generic'))
    } finally {
      setBusy(false)
    }
  }

  const another = () => {
    setForm({ ...EMPTY, country: tenant?.defaultCountry ?? '' })
    setErrors({})
    setResult(null)
    setSubmitError(null)
    setPhase('ready')
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const relLabel = (v: string) => t(`relatedPerson.relationship.${v}`)
  const input = (key: Field, label: string, opts: { required?: boolean; type?: string; autoComplete?: string; maxLength?: number; placeholder?: string; onBlur?: (v: string) => string } = {}) => (
    <div>
      <label htmlFor={`rp-${key}`} className="label-text">{label}{opts.required && <span className="text-destructive"> *</span>}</label>
      <input
        id={`rp-${key}`}
        type={opts.type ?? 'text'}
        value={String(form[key] ?? '')}
        onChange={e => set(key, e.target.value as never)}
        onBlur={opts.onBlur ? e => { const v = opts.onBlur!(e.target.value); if (v !== e.target.value) setForm(prev => ({ ...prev, [key]: v })) } : undefined}
        autoComplete={opts.autoComplete ?? 'off'}
        maxLength={opts.maxLength ?? 200}
        placeholder={opts.placeholder}
        aria-invalid={errors[key] ? true : undefined}
        className={cn('input-field', errors[key] && 'border-destructive focus:ring-destructive')}
      />
      {errors[key] && <p className="text-small text-destructive mt-1">{errors[key]}</p>}
    </div>
  )
  const choice = (key: 'referencePerson' | 'confidentialityWaiver' | 'contactAgreed', question: string, yes: string, no: string, hintYes?: string, hintNo?: string) => (
    <fieldset>
      <legend className="text-body text-foreground mb-2">{question} <span className="text-destructive">*</span></legend>
      <div className="flex flex-col sm:flex-row gap-2">
        {(['yes', 'no'] as const).map(v => (
          <label key={v} className={cn('flex items-center gap-2 px-3 py-2 rounded-input border cursor-pointer text-body', form[key] === v ? 'border-primary bg-primary/5' : 'border-border')}>
            <input type="radio" name={`rp-${key}`} value={v} checked={form[key] === v} onChange={() => set(key, v)} />
            <span>{v === 'yes' ? yes : no}</span>
          </label>
        ))}
      </div>
      {hintYes && form[key] === 'yes' && <p className="text-small text-muted-foreground mt-2 flex items-start gap-2"><PenLine className="w-4 h-4 flex-shrink-0 mt-0.5" /> {hintYes}</p>}
      {hintNo && form[key] === 'no' && <p className="text-small text-amber-800 bg-amber-50 rounded-input px-3 py-2 mt-2 flex items-start gap-2"><AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" /> {hintNo}</p>}
      {errors[key] && !(hintNo && form[key] === 'no') && <p className="text-small text-destructive mt-1">{errors[key]}</p>}
    </fieldset>
  )
  const section = (title: string, children: React.ReactNode, note?: string) => (
    <section className="space-y-4">
      <h2 className="text-body font-semibold text-foreground border-b border-border pb-1">{title}</h2>
      {note && <p className="text-small text-muted-foreground">{note}</p>}
      {children}
    </section>
  )

  const patientName = [link?.firstName, link?.lastName].filter(Boolean).join(' ')
  // The person just saved, for the step-2 screen
  const savedPerson = result ? [form.firstName, form.lastName].filter(Boolean).join(' ') : ''
  const addressStarted = [form.street, form.houseNumber, form.zipCode, form.city].some(v => v.trim())
  // Without the patient's release and the person's agreement nothing may be sent (Paul, 2026-10-15)
  const consentDeclined = form.confidentialityWaiver === 'no' || form.contactAgreed === 'no'
  const countries = buildCountriesList(i18n.language, t('relatedPerson.otherCountry'))
  const privacyUrl = tenant?.privacyUrl
  const termsUrl = tenant?.termsUrl

  return (
    <div className="min-h-screen bg-muted flex flex-col">
      <main className="flex-1 flex items-start justify-center p-4 py-8">
        <div className="bg-white rounded-card shadow-sm p-6 sm:p-8 max-w-2xl w-full">
          <div className="flex justify-center mb-6"><Logo size={100} /></div>

          {phase === 'loading' && (
            <div className="text-center py-8 text-muted-foreground flex items-center justify-center gap-2">
              <Loader2 className="w-5 h-5 animate-spin" /> {t('common.loading')}
            </div>
          )}

          {(phase === 'invalid' || phase === 'expired' || phase === 'revoked' || phase === 'disabled') && (
            <div className="text-center py-4">
              <AlertTriangle className="w-10 h-10 text-amber-500 mx-auto mb-3" />
              <h1 className="text-h2 text-foreground mb-2">{t(`relatedPerson.${phase}.title`)}</h1>
              <p className="text-body text-muted-foreground">{t(`relatedPerson.${phase}.body`)}</p>
              {tenant?.contactEmail && (
                <p className="text-small text-muted-foreground mt-4">{t('relatedPerson.contact')} <a className="underline" href={`mailto:${tenant.contactEmail}`}>{tenant.contactEmail}</a></p>
              )}
            </div>
          )}

          {(phase === 'ready' || phase === 'done') && link && (
            <>
              {/* Two steps: name the person here, then sign the Schweigepflichtentbindung (/informed-consent) */}
              <StepIndicator
                steps={[t('relatedPerson.steps.one'), t('relatedPerson.steps.two')]}
                current={phase === 'done' && result?.waiver?.status !== 'already_signed' ? 1 : phase === 'done' ? 2 : 0}
                caption={t('relatedPerson.steps.caption', { n: phase === 'done' ? 2 : 1 })}
              />
              {phase === 'ready' && <h1 className="text-h2 text-foreground mb-2 text-center">{t('relatedPerson.title')}</h1>}
{phase === 'ready' && (
              <div className="bg-muted/50 rounded-card p-4 mb-5 text-small">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1">
                  <div><span className="text-muted-foreground">{t('relatedPerson.patientNumber')}:</span> <span className="font-medium">{link.patientNumber}</span></div>
                  {patientName && <div><span className="text-muted-foreground">{t('relatedPerson.patientName')}:</span> <span className="font-medium">{patientName}</span></div>}
                </div>
                <p className="text-muted-foreground mt-2">{t('relatedPerson.checkIdentity')}</p>
              </div>
              )}

              {phase === 'ready' && link.entries.length > 0 && (
                <div className="mb-6">
                  <h2 className="text-body font-medium text-foreground mb-2 flex items-center gap-2"><Users className="w-4 h-4" /> {t('relatedPerson.named')}</h2>
                  <ul className="space-y-2">
                    {link.entries.map(e => (
                      <li key={e.id} className="text-small border border-border rounded-card px-3 py-2 flex flex-wrap items-center justify-between gap-2">
                        <span><span className="font-medium">{e.firstName} {e.lastName}</span> <span className="text-muted-foreground">· {relLabel(e.relationshipType)}</span></span>
                        {e.waiver?.status === 'signed' && <span className="text-green-700 flex items-center gap-1"><CheckCircle2 className="w-4 h-4" /> {t('relatedPerson.waiverSigned')}</span>}
                        {e.waiver?.status === 'open' && e.waiver.url && <a href={e.waiver.url} className="text-primary underline flex items-center gap-1"><PenLine className="w-4 h-4" /> {t('relatedPerson.signNow')}</a>}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {phase === 'done' && result && (
                <div className="mb-6" role="status">
                  <p className="text-small text-green-800 bg-green-50 rounded-input px-3 py-2 flex items-start gap-2 mb-5">
                    <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5" /> {t('relatedPerson.step1Done', { name: savedPerson })}
                  </p>
                  {result.waiver?.status === 'ready' && result.waiver.url && (
                    <div className="rounded-card border-2 border-primary p-5 text-center">
                      <h2 className="text-h3 text-foreground mb-2">{t('relatedPerson.step2Title')}</h2>
                      <p className="text-body text-foreground mb-1">{t('relatedPerson.step2Body', { name: savedPerson })}</p>
                      {result.waiver.phoneMasked && <p className="text-small text-muted-foreground mb-1">{t('relatedPerson.step2Phone', { phone: result.waiver.phoneMasked })}</p>}
                      <Button onClick={() => { window.location.href = result.waiver!.url! }} className="mt-4 w-full inline-flex items-center justify-center gap-2">
                        <PenLine className="w-4 h-4" /> {t('relatedPerson.waiverButton')}
                      </Button>
                    </div>
                  )}
                  {result.waiver?.status === 'already_signed' && <p className="text-body text-foreground">{t('relatedPerson.waiverAlready')}</p>}
                  {result.waiver?.status === 'not_started' && <p className="text-body text-foreground">{t('relatedPerson.waiverLater')}</p>}
                  {link.remaining > 0 && result.waiver?.status !== 'ready' && (
                    <button type="button" onClick={another} className="mt-4 text-small text-primary underline flex items-center gap-1">
                      <UserPlus className="w-4 h-4" /> {t('relatedPerson.another')}
                    </button>
                  )}
                </div>
              )}

              {phase === 'ready' && (link.remaining <= 0 ? (
                <p className="text-body text-muted-foreground">{t('relatedPerson.errors.tooMany')}</p>
              ) : (
                <form onSubmit={submit} noValidate className="space-y-8">
                  <div className="text-body text-foreground space-y-3">
                    <p>{t('relatedPerson.intro1')}</p>
                    <p>{t('relatedPerson.intro2')}</p>
                  </div>

                  {section(t('relatedPerson.sections.person'), (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {input('firstName', t('relatedPerson.fields.firstName'), { required: true, autoComplete: 'off', maxLength: 50, onBlur: formatName })}
                      {input('lastName', t('relatedPerson.fields.lastName'), { required: true, autoComplete: 'off', maxLength: 50, onBlur: formatName })}
                      <div>
                        <label htmlFor="rp-gender" className="label-text">{t('relatedPerson.fields.gender')} <span className="text-destructive">*</span></label>
                        <select id="rp-gender" value={form.gender ?? ''} onChange={e => set('gender', (e.target.value || null) as RelatedPersonAnswers['gender'])} aria-invalid={errors.gender ? true : undefined}
                          className={cn('input-field', errors.gender && 'border-destructive focus:ring-destructive')}>
                          <option value="">{t('relatedPerson.select')}</option>
                          {GENDERS.map(g => <option key={g} value={g}>{t(`relatedPerson.gender.${g}`)}</option>)}
                        </select>
                        {errors.gender && <p className="text-small text-destructive mt-1">{errors.gender}</p>}
                      </div>
                    </div>
                  ))}

                  {section(t('relatedPerson.sections.relationship'), (
                    <>
                      <SearchableSelect
                        id="rp-relationshipType"
                        label={`${t('relatedPerson.fields.relationshipType')} *`}
                        value={form.relationshipType}
                        onChange={v => set('relationshipType', v)}
                        error={errors.relationshipType}
                        options={[{ value: '', label: t('relatedPerson.select') }, ...RELATIONSHIPS.map(r => ({ value: r, label: relLabel(r) }))]}
                      />
                      {form.relationshipType === 'other' && input('relationshipNote', t('relatedPerson.fields.relationshipNoteOther'), { required: true, maxLength: 500 })}
                      {choice('referencePerson', t('relatedPerson.fields.referencePerson'), t('relatedPerson.yes'), t('relatedPerson.no'))}
                      {LEGAL_ROLES.has(form.relationshipType) && input('scope', t('relatedPerson.fields.scopeLegal'), { maxLength: 500, placeholder: t('relatedPerson.placeholders.scope') })}
                    </>
                  ))}

                  {section(t('relatedPerson.sections.contact'), (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {input('phone', t('relatedPerson.fields.phone'), { type: 'tel', maxLength: 30, placeholder: t('relatedPerson.placeholders.phone'), onBlur: v => (v.trim() ? normalizePhoneNumberOnBlur(v, tenant?.phonePrefix ?? '+49') : v) })}
                      {input('email', t('relatedPerson.fields.email'), { type: 'email', maxLength: 254, placeholder: t('relatedPerson.placeholders.email') })}
                    </div>
                  ), t('relatedPerson.notes.contact'))}

                  {section(t('relatedPerson.sections.address'), (
                    <div className="grid grid-cols-1 sm:grid-cols-[1fr_8rem] gap-4">
                      {input('street', t('relatedPerson.fields.street'), { required: addressStarted })}
                      {input('houseNumber', t('relatedPerson.fields.houseNumber'), { required: addressStarted, maxLength: 20 })}
                      {input('zipCode', t('relatedPerson.fields.zipCode'), { required: addressStarted, maxLength: 10, onBlur: v => (v.trim() ? formatPostalCode(v, form.country || 'Other') : v) })}
                      <div className="sm:col-span-2 grid grid-cols-1 sm:grid-cols-3 gap-4">
                        {input('city', t('relatedPerson.fields.city'), { required: addressStarted, maxLength: 100 })}
                        {input('state', t('relatedPerson.fields.state'), { maxLength: 100 })}
                        <SearchableSelect
                          id="rp-country"
                          label={t('relatedPerson.fields.country')}
                          value={form.country}
                          onChange={v => set('country', v)}
                          options={countries}
                        />
                      </div>
                    </div>
                  ), t('relatedPerson.notes.address'))}

                  {section(t('relatedPerson.sections.consents'), (
                    <>
                      {choice('confidentialityWaiver', t('relatedPerson.fields.confidentialityWaiver'), t('relatedPerson.waiverYes'), t('relatedPerson.waiverNo'), t('relatedPerson.waiverHint'), t('relatedPerson.errors.waiverNeeded'))}
                      {choice('contactAgreed', t('relatedPerson.fields.contactAgreed'), t('relatedPerson.contactYes'), t('relatedPerson.contactNo'), undefined, t('relatedPerson.errors.contactAgreedNeeded'))}
                      <div>
                        <label className="flex items-start gap-3 text-body text-foreground cursor-pointer">
                          <input type="checkbox" className="mt-1" checked={form.privacyAccepted} onChange={e => set('privacyAccepted', e.target.checked)} />
                          <span>
                            {t('relatedPerson.privacyBefore')}{' '}
                            {privacyUrl ? <a href={privacyUrl} target="_blank" rel="noopener noreferrer" className="underline">{t('relatedPerson.privacyLink')}</a> : t('relatedPerson.privacyLink')}{' '}
                            {t('relatedPerson.privacyMiddle')}{' '}
                            {termsUrl ? <a href={termsUrl} target="_blank" rel="noopener noreferrer" className="underline">{t('relatedPerson.termsLink')}</a> : t('relatedPerson.termsLink')}{' '}
                            {t('relatedPerson.privacyAfter')} <span className="text-destructive">*</span>
                          </span>
                        </label>
                        {errors.privacyAccepted && <p className="text-small text-destructive mt-1">{errors.privacyAccepted}</p>}
                      </div>
                    </>
                  ), t('relatedPerson.notes.consents'))}

                  {submitError && <p className="text-small text-destructive flex items-start gap-2" role="alert"><AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" /> {submitError}</p>}
                  <Button type="submit" disabled={busy || consentDeclined} className="w-full">{busy ? t('common.sending') : t('relatedPerson.submit')}</Button>
                  <p className="text-small text-muted-foreground flex items-start gap-2"><ShieldCheck className="w-4 h-4 flex-shrink-0 mt-0.5" /> {t('relatedPerson.footerNote')}</p>
                </form>
              ))}
            </>
          )}
        </div>
      </main>
      <LegalFooter />
    </div>
  )
}
